import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import * as color from '@constant/color';
import * as size from '@constant/size';
import { ETheme } from '@datatype/type';
import { EApiLanguageCode } from '@datatype/iyagigo/api-type';
import TextBlock from '@component/common/block/text-block';
import TextButton from '@design-system/text-button';
import Gap from '@component/common/gap';
import { useClassroomExpressionWorkbooksQuery } from '@hook/iyagigo/useApi';
import { useTypedDispatch, useTypedSelector } from '@hook/common/useStore';
import { actions as modalActions } from '@store/slice/modal-slice';
import { actions as classroomActions } from '@store/slice/classroom-slice';
import { CLASSROOM_EXPRESSION_GROUP_NAME } from '@constant/iyagigo/variable';

const Wrapper = styled.div`
  display: flex;
  flex-flow: column nowrap;
  align-items: center;
  width: ${size.MEDIUM_MODAL_WIDTH};
`;

const OptionContainer = styled.div`
  display: flex;
  flex-flow: column nowrap;
  gap: 5px;
  width: 100%;
`;

const OptionItem = styled.div<{ $selected: boolean }>`
  display: flex;
  align-items: center;
  width: 100%;
  padding: 8px 12px;
  border-radius: ${size.BORDER_RADIUS_2};
  border: ${size.BORDER_WIDTH_1} solid ${({ $selected }) => ($selected ? color.BROWN_1 : color.GRAY_BOX_BORDER_COLOR)};
  cursor: pointer;
`;

const ClassroomExpressionGroupSelectModal: React.FC = () => {
  const dispatch = useTypedDispatch();
  const storedGroupKey =
    useTypedSelector((state) => state.rootReducer?.classroomReducer?.currentClassroomExpressionGroupKey) ?? null;
  const studyLanguage =
    useTypedSelector((state) => state.rootReducer?.studentReducer?.currentStudyLanguage) ?? EApiLanguageCode.JAPANESE;
  const { data: workbooksResponse } = useClassroomExpressionWorkbooksQuery(studyLanguage);
  const expressions = (workbooksResponse?.data ?? []).map((workbook) => workbook.basicExpression);
  const groups = expressions.reduce<{ key: string; name: string }[]>((result, expression) => {
    if (result.some((group) => group.key === expression.groupKey)) return result;
    result.push({
      key: expression.groupKey,
      name: CLASSROOM_EXPRESSION_GROUP_NAME[expression.groupKey] ?? expression.groupKey,
    });
    return result;
  }, []);
  const currentGroupKey = groups.some((group) => group.key === storedGroupKey) ? storedGroupKey : groups[0]?.key;
  const [selectedKey, setSelectedKey] = useState<string>(currentGroupKey ?? '');

  useEffect(() => {
    if (!selectedKey && currentGroupKey) setSelectedKey(currentGroupKey);
  }, [currentGroupKey, selectedKey]);

  const handleConfirm = () => {
    if (!selectedKey) return;
    dispatch(classroomActions.storeCurrentClassroomExpressionGroupKey(selectedKey));
    dispatch(modalActions.hideModal());
  };

  return (
    <Wrapper>
      <TextBlock
        text='학습할 표현을 선택해주세요'
        design={{ text: { size: 'medium', color: color.BLACK_2 }, box: { alignX: 'center' } }}
      />
      <Gap height={size.MARGIN_1} />
      <OptionContainer>
        {groups.map((group) => (
          <OptionItem key={group.key} $selected={group.key === selectedKey} onClick={() => setSelectedKey(group.key)}>
            <TextBlock
              text={group.name}
              design={{ text: { size: 'medium', color: color.BLACK_2 }, box: { alignX: 'left' } }}
            />
          </OptionItem>
        ))}
      </OptionContainer>
      <Gap height={size.MARGIN_2} />
      <TextButton
        text='학습하기'
        theme={ETheme.BROWN}
        size='medium'
        shape='pill'
        width='100%'
        handleClick={handleConfirm}
      />
    </Wrapper>
  );
};

export default ClassroomExpressionGroupSelectModal;
